import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Circle, Marker, Popup } from "react-leaflet";
import { X, Target, Users, MapPin, TrendingUp, DollarSign, Globe } from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix para iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Datos de geo-targeting por mercado
const MARKET_GEO_DATA = {
  US: {
    name: "Estados Unidos",
    flag: "🇺🇸",
    center: [39.8283, -98.5795],
    zoom: 4,
    hotspots: [
      { city: "Los Angeles", coords: [34.0522, -118.2437], population: "3.9M", density: 95, spend: "$45" },
      { city: "New York", coords: [40.7128, -74.0060], population: "8.3M", density: 92, spend: "$52" },
      { city: "Houston", coords: [29.7604, -95.3698], population: "2.3M", density: 78, spend: "$38" },
      { city: "Phoenix", coords: [33.4484, -112.0740], population: "1.6M", density: 72, spend: "$35" },
      { city: "Miami", coords: [25.7617, -80.1918], population: "470K", density: 88, spend: "$48" },
    ],
    totalReach: "45M",
    avgCPM: "$8.50",
    demographics: { age: "25-44", gender: "60% F / 40% M", income: "$50K-$100K" }
  },
  UK: {
    name: "Reino Unido",
    flag: "🇬🇧",
    center: [55.3781, -3.4360],
    zoom: 5,
    hotspots: [
      { city: "London", coords: [51.5074, -0.1278], population: "8.9M", density: 96, spend: "£42" },
      { city: "Manchester", coords: [53.4808, -2.2426], population: "550K", density: 82, spend: "£35" },
      { city: "Birmingham", coords: [52.4862, -1.8904], population: "1.1M", density: 78, spend: "£32" },
      { city: "Liverpool", coords: [53.4084, -2.9916], population: "500K", density: 75, spend: "£30" },
    ],
    totalReach: "18M",
    avgCPM: "£6.20",
    demographics: { age: "25-44", gender: "55% F / 45% M", income: "£35K-£70K" }
  },
  DE: {
    name: "Alemania",
    flag: "🇩🇪",
    center: [51.1657, 10.4515],
    zoom: 5,
    hotspots: [
      { city: "Berlin", coords: [52.5200, 13.4050], population: "3.6M", density: 88, spend: "€38" },
      { city: "Munich", coords: [48.1351, 11.5820], population: "1.5M", density: 85, spend: "€42" },
      { city: "Hamburg", coords: [53.5511, 9.9937], population: "1.9M", density: 80, spend: "€36" },
      { city: "Frankfurt", coords: [50.1109, 8.6821], population: "750K", density: 82, spend: "€40" },
    ],
    totalReach: "22M",
    avgCPM: "€7.80",
    demographics: { age: "30-50", gender: "52% F / 48% M", income: "€40K-€80K" }
  },
  GLOBAL: {
    name: "Global",
    flag: "🌍",
    center: [20, 0],
    zoom: 2,
    hotspots: [
      { city: "New York", coords: [40.7128, -74.0060], population: "8.3M", density: 92, spend: "$52" },
      { city: "London", coords: [51.5074, -0.1278], population: "8.9M", density: 96, spend: "$42" },
      { city: "Tokyo", coords: [35.6762, 139.6503], population: "13.9M", density: 94, spend: "$35" },
      { city: "Sydney", coords: [-33.8688, 151.2093], population: "5.3M", density: 78, spend: "$38" },
    ],
    totalReach: "120M",
    avgCPM: "$6.50",
    demographics: { age: "18-45", gender: "55% F / 45% M", income: "Varies" }
  },
  AU: {
    name: "Australia",
    flag: "🇦🇺",
    center: [-25.2744, 133.7751],
    zoom: 4,
    hotspots: [
      { city: "Sydney", coords: [-33.8688, 151.2093], population: "5.3M", density: 85, spend: "A$42" },
      { city: "Melbourne", coords: [-37.8136, 144.9631], population: "5.0M", density: 82, spend: "A$40" },
      { city: "Brisbane", coords: [-27.4698, 153.0251], population: "2.5M", density: 75, spend: "A$35" },
    ],
    totalReach: "12M",
    avgCPM: "A$9.20",
    demographics: { age: "25-44", gender: "58% F / 42% M", income: "A$60K-A$120K" }
  }
};

export default function GeoTargetingModal({ isOpen, onClose, markets = ["US"], product, microCommunityScore, marketSize, audienceReach }) {
  const [selectedMarket, setSelectedMarket] = useState(markets[0] || "US");
  const [mapKey, setMapKey] = useState(0);
  
  const marketData = MARKET_GEO_DATA[selectedMarket] || MARKET_GEO_DATA["US"];
  
  useEffect(() => {
    // Force map re-render when market changes
    setMapKey(prev => prev + 1);
  }, [selectedMarket]);

  if (!isOpen) return null;

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start sm:items-center justify-center p-2 sm:p-4">
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-5xl my-2 sm:my-4">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10 rounded-t-2xl">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-violet-500/20 rounded-xl flex items-center justify-center">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">Georeferenciación</h2>
              <p className="text-xs sm:text-sm text-zinc-400">{product?.name || "Producto"}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Sidebar con métricas */}
          <div className="lg:w-80 p-3 sm:p-4 border-b lg:border-b-0 lg:border-r border-zinc-800 space-y-3 sm:space-y-4">
            {/* Selector de mercado */}
            <div>
              <label className="text-xs text-zinc-500 mb-2 block">Seleccionar Mercado</label>
              <div className="flex flex-wrap gap-2">
                {markets.map(market => (
                  <button
                    key={market}
                    onClick={() => setSelectedMarket(market)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedMarket === market
                        ? "bg-violet-500 text-white"
                        : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                    }`}
                  >
                    {MARKET_GEO_DATA[market]?.flag} {market}
                  </button>
                ))}
              </div>
            </div>

            {/* Métricas del mercado */}
            <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Globe className="w-4 h-4 text-violet-400" />
                {marketData.flag} {marketData.name}
              </h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-900/50 rounded-lg p-3">
                  <div className="text-xl font-bold text-cyan-400">{marketData.totalReach}</div>
                  <div className="text-xs text-zinc-500">Alcance Total</div>
                </div>
                <div className="bg-zinc-900/50 rounded-lg p-3">
                  <div className="text-xl font-bold text-emerald-400">{marketData.avgCPM}</div>
                  <div className="text-xs text-zinc-500">CPM Promedio</div>
                </div>
              </div>
            </div>

            {/* Índice de Micro-Comunidad */}
            <div className="bg-gradient-to-br from-violet-500/20 to-pink-500/20 rounded-xl p-4 border border-violet-500/30">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-5 h-5 text-violet-400" />
                <span className="text-sm font-medium text-white">Índice Micro-Comunidad</span>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-4xl font-bold text-violet-400">{microCommunityScore || 85}</span>
                <span className="text-zinc-400 mb-1">/100</span>
              </div>
              <div className="mt-2 h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-violet-500 to-pink-500 rounded-full transition-all duration-500"
                  style={{ width: `${microCommunityScore || 85}%` }}
                />
              </div>
            </div>

            {/* Demografía */}
            <div className="bg-zinc-800/50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Demografía Target</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Edad</span>
                  <span className="text-white">{marketData.demographics.age}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Género</span>
                  <span className="text-white">{marketData.demographics.gender}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Ingreso</span>
                  <span className="text-white">{marketData.demographics.income}</span>
                </div>
              </div>
            </div>

            {/* Hotspots */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-pink-400" />
                Ciudades con Mayor Densidad
              </h3>
              <div className="space-y-2">
                {marketData.hotspots.map((spot, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-3 py-2">
                    <div>
                      <div className="text-sm text-white">{spot.city}</div>
                      <div className="text-xs text-zinc-500">{spot.population} habitantes</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-emerald-400">{spot.density}%</div>
                      <div className="text-xs text-zinc-500">densidad</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mapa - Solo visible en desktop */}
          <div className="hidden lg:block flex-1 h-[450px] relative">
            <MapContainer
              key={mapKey}
              center={marketData.center}
              zoom={marketData.zoom}
              style={{ height: "100%", width: "100%" }}
              className="rounded-none lg:rounded-br-2xl"
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              
              {/* Círculos de calor para cada hotspot */}
              {marketData.hotspots.map((spot, idx) => (
                <Circle
                  key={idx}
                  center={spot.coords}
                  radius={spot.density * 5000}
                  pathOptions={{
                    color: "#8b5cf6",
                    fillColor: "#8b5cf6",
                    fillOpacity: 0.3,
                    weight: 2
                  }}
                >
                  <Popup>
                    <div className="text-center p-1">
                      <div className="font-bold text-lg">{spot.city}</div>
                      <div className="text-sm text-gray-600">Población: {spot.population}</div>
                      <div className="text-sm text-gray-600">Densidad: {spot.density}%</div>
                      <div className="text-sm font-semibold text-violet-600">Gasto Prom: {spot.spend}</div>
                    </div>
                  </Popup>
                </Circle>
              ))}

              {/* Marcadores */}
              {marketData.hotspots.map((spot, idx) => (
                <Marker key={`marker-${idx}`} position={spot.coords}>
                  <Popup>
                    <div className="text-center p-1">
                      <div className="font-bold text-lg">{spot.city}</div>
                      <div className="text-sm text-gray-600">Población: {spot.population}</div>
                      <div className="text-sm text-gray-600">Densidad de audiencia: {spot.density}%</div>
                      <div className="text-sm font-semibold text-violet-600">Gasto promedio: {spot.spend}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* Leyenda del mapa */}
            <div className="absolute bottom-4 left-4 bg-zinc-900/90 backdrop-blur-sm rounded-xl p-3 border border-zinc-700 z-[1000]">
              <div className="text-xs text-zinc-400 mb-2">Leyenda</div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded-full bg-violet-500/50 border border-violet-500" />
                <span className="text-white">Zona de alta densidad</span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <MapPin className="w-4 h-4 text-blue-500" />
                <span className="text-white">Ciudad principal</span>
              </div>
            </div>

            {/* Stats overlay */}
            <div className="absolute top-4 right-4 bg-zinc-900/90 backdrop-blur-sm rounded-xl p-3 border border-zinc-700 z-[1000]">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-lg font-bold text-cyan-400">{formatNumber(marketSize || 240000)}</div>
                  <div className="text-xs text-zinc-500">Tamaño Mercado</div>
                </div>
                <div className="w-px h-10 bg-zinc-700" />
                <div>
                  <div className="text-lg font-bold text-pink-400">{formatNumber(audienceReach || 36000)}</div>
                  <div className="text-xs text-zinc-500">Alcance Ads</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs sm:text-sm text-zinc-400 text-center sm:text-left">
            Los círculos representan la densidad de la micro-comunidad
          </div>
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-violet-500 text-white font-medium rounded-xl hover:bg-violet-400 transition-colors"
          >
            Entendido
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
